<template>
  <div class="the-user-menu the-user-menu--signed the-user-menu">
    <div class="the-user-menu__welcome">
      <span class="the-user-menu__welcome__title">{{ fullName }} <br /></span>
      <small class="the-user-menu__welcome__subtitle uppercase">
        <span class="the-user-menu__welcome__subtitle__icon material-icons">
          security
        </span>
        {{ userLoggedRol }}
      </small>
    </div>
    <div class="drop__toggler"></div>
    <div class="drop__toggler">
      <Button
        class="p-button-icon-only the-user-menu__toggler"
        @click="showMenu = !showMenu"
      >
        <span class="the-user-menu__toggler__icon material-icons">
          person
        </span>
      </Button>
    </div>

    <div class="drop__content" :class="{ show: showMenu }">
      <div class="the-user-menu__drop profile">
        <div class="profile__body">
          <div class="profile__body__abbr">{{ initials }}</div>
          <div class="profile__body__data">
            <div class="profile__body__data__main">
              <h3 class="profile__body__data__main__title">
                {{ fullName }}
              </h3>
              <p class="profile__body__data__main__info">
                <span class="material-icons"> mail </span>
                {{ userLogged.email }}
              </p>
              <!-- #region Role -->
              <p class="profile__body__data__main__info">
                <span class="material-icons"> security </span>
                {{ userLoggedRol }}
              </p>
              <div
                v-if="userLoggedRol == 'Admin'"
                @click="changeLayout()"
                class="change-panel"
              >
                <i class="pi pi-external-link"></i>
                {{ changePanelText }}
              </div>
              <div class="flex justify-content-end">
                <Button
                  size="small"
                  class="mt-2"
                  @click="clearAuthData({ manualLogout: true })"
                  label="Cerrar sesión"
                  severity="secondary"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="profile__footer"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Button from "primevue/button";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useAuthorization } from "@sttedra_ui/framework";

import { clearAuthData } from "@/composables/_commons/auth.js";

// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const userLogged = computed(() => authorizationModelRef.value);
const userLoggedRol = computed(() => userLogged.value?.roles[0]);

const layout = computed(() => store.getters.layout);
const changePanelText = computed(() =>
  layout.value == "admin" ? "Modo usuario" : "Modo administrador"
);

const fullName = computed(() => {
  let name = "";

  if (userLogged.value?.user?.apellido1)
    name += userLogged.value.user.apellido1;
  if (userLogged.value?.user?.apellido2)
    name += ` ${userLogged.value.user.apellido2}`;
  if (userLogged.value?.user?.nombre)
    name += ` ${userLogged.value.user.nombre}`;

  return toTitleCase(name);
});

const initials = computed(() => {
  let initials = fullName.value
    .split(" ")
    .map((n) => n[0])
    .join("");

  if (initials.length > 2) {
    initials = initials.substring(0, 2);
  }

  return initials;
});

const { modelRef: authorizationModelRef } = useAuthorization();
const store = useStore();
const router = useRouter();
const showMenu = ref(false);
// #endregion

// #region Functions
const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

function changeLayout() {
  let newLayout = layout.value == "admin" ? "web" : "admin";

  const currentRoute = router.currentRoute.value;

  if (currentRoute.name == "Public reports") {
    newLayout = "admin";
  }

  store.commit("setLayout", newLayout);
  localStorage.setItem("layout", newLayout);

  if (newLayout == "web") {
    router.push({
      name: "Public reports",
    });
  } else {
    router.push({
      name: "Reports",
    });
  }
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  // Listen DOM click, if not in the user menu, close it
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".the-user-menu")) {
      showMenu.value = false;
    }
  });
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.the-user-menu {
  position: relative;
  align-items: center;
  display: flex;
  flex: 1 0 auto;

  .the-user-menu__welcome {
    align-items: flex-end;
    color: white;
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    justify-content: center;
    margin: 0 1rem;
    white-space: nowrap;

    .the-user-menu__welcome__title {
      text-align: right;
    }

    .the-user-menu__welcome__subtitle {
      align-items: center;
      border-radius: 0.2rem;
      display: flex;
      font-weight: bolder;

      .the-user-menu__welcome__subtitle__icon {
        font-size: 0.9rem;
        margin-right: 0.2rem;
      }
    }
  }

  .drop__content {
    position: absolute;
    top: 100%;
    right: 0;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
    z-index: 999999;
    transition: all 0.3s ease;
    visibility: hidden;
    opacity: 0;

    &.show {
      visibility: visible;
      opacity: 1;
    }

    .the-user-menu__drop {
      background-color: #ffffff;
      min-width: 250px;

      .profile__header {
        align-items: center;
        display: flex;
        justify-content: space-between;
        padding-left: 0.5rem;
        width: 100%;

        .profile__header__brand {
          align-items: center;
          display: flex;
          justify-content: center;
          width: 3.8rem;
        }
      }

      .profile__body {
        display: flex;
        align-items: stretch;
        justify-content: flex-start;

        .profile__body__abbr {
          align-items: center;
          background-color: $primary;
          border-radius: 100%;
          color: #ffffff;
          display: flex;
          height: 4rem;
          flex: 0 0 auto;
          font-size: 2rem;
          justify-content: center;
          text-align: center;
          width: 4rem;
        }

        .profile__body__data {
          display: flex;
          align-items: center;
          flex: 1 1 100%;
          justify-content: space-between;

          .profile__body__data__main {
            max-width: unset;

            .profile__body__data__main__title {
              font-size: 13px;
            }

            .profile__body__data__main__info {
              align-items: center;
              display: flex;
              font-size: 12px;
              font-weight: 400;
              margin: 0;
              max-width: 100%;

              .material-icons {
                color: #89a1ac;
                font-size: 14px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
